import React, {useCallback, useEffect} from 'react';
import cx from 'classnames';
import {observer} from 'mobx-react';
import {
    Flex,
    noop,
    TabsMenu,
    TabsSkinEnum,
    TabMenuItem
} from '@priority-software/priority-style-react';
import {Form} from '../../../../stores/Form/Form';
import {FormControl} from './FormControl';
import {navigation} from './utils/navigation';

import {FormActiveElement} from '../../../../stores/Form/Form';
import {handleFocusAction} from './actions';
import {createDataTestId} from '../../../../utils/dataTestId';
import {IFormViewSection, useFormView} from './useFormView';
import {BIDropdown} from '../BIDropdown';
import {isNavigationLocked} from '../../utils/isNavigationLocked';
import {priority} from '@priority-software/priority-proto-api/lib';
import {FormViewImageField} from '../FormViewImageField/FormViewImageField';
import {texts} from '../AdvancedSearch/AdvancedSearchDrawer/texts';
import {Column, Field} from '../../../../stores/Form';
import {handleFormBooleanValue} from '../../actions/utils/handleFormBooleanValue';
import styles from './FormView.module.scss';

type Props = {
    currentForm: Form;
    isActiveForm: boolean;
    sectionClass?: string;
    componentClasses?: string;
};

export const FormView = observer((props: Props) => {
    const {currentForm, isActiveForm, sectionClass, componentClasses} = props;
    const {searchModeLevel, currentRow, currentColumnId, records} = currentForm;
    const {tabs, activeTab, setActiveTab} = useFormView(currentForm);
    const currentRowData = records[currentRow];

    const handleKeydown = async e => {
        if (currentForm && !isNavigationLocked(currentForm)) {
            navigation(e, currentForm, tabs, activeTab, async navData => {
                if (navData && navData.Column) {
                    const isAllowed = await handleFocusAction(currentForm, navData.Column.index);

                    if (isAllowed) {
                        currentForm.setCurrentColumnId(navData.Column.id);
                        if (
                            navData.ActiveTab &&
                            activeTab !== navData.ActiveTab &&
                            navData.ActiveTab !== tabs[0].Id
                        ) {
                            setActiveTab(navData.ActiveTab);
                        }
                    }
                }
            });
        }
    };

    const updateCurrentValue = useCallback(
        async (currField: Field, value: string | boolean, column: Column) => {
            const isCheckboxType = column.Type === priority.netitems.form.ColumnType.Boolean;
            if (isCheckboxType) {
                await handleFormBooleanValue(column, currField);
            } else {
                currField.setCurrentValue(value as string);
            }
        },
        []
    );

    const onTabChange = useCallback(
        async (id: string) => {
            const activeTab = tabs.find(tab => tab.Id === id);
            const newActiveCol = activeTab.Sections?.[0].Rows?.[0]?.[0].Column;

            if (newActiveCol) {
                const isAllowed = await handleFocusAction(currentForm, newActiveCol.index);

                if (isAllowed) {
                    currentForm.setCurrentColumnId(newActiveCol.id);
                    setActiveTab(id);
                }
            }
        },
        [currentForm, tabs]
    );

    const onFocus = useCallback(
        async (columnIndex: number, el: FormActiveElement) => {
            const prevActiveEl = currentForm.formActiveElement;
            currentForm.setFormActiveElement(el);
            const isOk = await handleFocusAction(currentForm, columnIndex);
            if (!isOk) {
                currentForm.setFormActiveElement(prevActiveEl);
            }
        },
        [currentForm]
    );

    const renderImageField = () => {
        if (currentForm?.formDesign?.showPicture) {
            const {columns} = currentForm;

            const attachmentColumn = columns.find(
                col => col.Type === priority.netitems.form.ColumnType.Attachment
            );
            if (attachmentColumn) {
                const currentRowData = currentForm.records[currentRow];
                const imageData = currentRowData?.getField(attachmentColumn.id)?.Value?.File;
                return (
                    <div
                        key={attachmentColumn.id}
                        className={cx(styles.fieldSetRow, styles.alignItemsTop)}
                        {...createDataTestId(`column-name-${attachmentColumn.Name}`, false)}
                    >
                        <div className={cx(styles.fieldSetLabel)}>
                            <span
                                className={cx({'pui-label-required': attachmentColumn.Mandatory})}
                            >
                                {attachmentColumn.Title}
                            </span>
                        </div>

                        <div className={styles.controlsWrap}>
                            <FormViewImageField
                                placeholder={texts.uploadImagePlaceholder}
                                column={attachmentColumn}
                                file={imageData}
                                disabled={
                                    currentForm?.searchModeLevel > 0 || attachmentColumn.isReadonly
                                }
                            />
                        </div>
                    </div>
                );
            }
        }
        return null;
    };

    const renderControls = (tabSection: IFormViewSection) => {
        if (!tabSection) return null;

        return tabSection.Rows.map((Row, index) => {
            if (Row.length === 0)
                return (
                    <div key={index} className={cx(styles.fieldSetRow, styles.rowPlaceholder)} />
                );

            const mainColumn = Row[0].Column;

            return (
                <div
                    key={mainColumn.id}
                    className={styles.fieldSetRow}
                    {...createDataTestId(`column-name-${mainColumn.Name}`, false)}
                >
                    <div className={cx(styles.fieldSetLabel)}>
                        <span className={cx({'pui-label-required': mainColumn.Mandatory})}>
                            {mainColumn.Title}
                        </span>
                        {/*To be implemented in future release */}
                        {false && <BIDropdown column={mainColumn} />}
                    </div>

                    <div className={styles.controlsWrap}>
                        {Row.map(control => {
                            const Column = control.Column;
                            const currField = currentRowData?.getField(Column.id);
                            const searchValue = currentForm.searchModeData[currentRow]?.[Column.id];

                            return (
                                <FormControl
                                    key={`${currentForm?.currentRecord?.clientKey}-${Column.id}-${currentForm.searchModeLevel}`}
                                    updateSearchValue={currentForm.updateSearchModeCellValue}
                                    updateCurrentValue={updateCurrentValue}
                                    searchValue={searchValue}
                                    column={Column}
                                    searchMode={!!searchModeLevel}
                                    isActive={isActiveForm && Column.id === currentColumnId}
                                    onFocus={onFocus}
                                    currentRow={currentRow}
                                    currField={currField}
                                />
                            );
                        })}
                    </div>
                </div>
            );
        });
    };

    useEffect(() => {
        if (isActiveForm) {
            window.addEventListener('keydown', handleKeydown, true);
            return () => window.removeEventListener('keydown', handleKeydown, true);
        }
        return noop;
    }, [isActiveForm, handleKeydown]);

    const tabData = tabs.find(tab => tab.Id === activeTab);
    const leftData = tabData?.Sections[0];
    const rightData = tabData?.Sections[1];

    return (
        <div
            className={cx(styles.component, componentClasses)}
            key={currentRowData?.clientKey}
            {...createDataTestId('form-single-view-container', false)}
        >
            <div
                className={cx(styles.mainArea, sectionClass)}
                {...createDataTestId('form-single-view-main-area', false)}
            >
                <div className={styles.fieldSet}>
                    {renderControls(tabs[0]?.Sections?.[0])}
                    {renderImageField()}
                </div>
            </div>

            <div className={cx(styles.additionalArea, sectionClass)}>
                <div className={styles.tabsWrap}>
                    <TabsMenu
                        bottomBorder={true}
                        skin={TabsSkinEnum.material}
                        {...createDataTestId('tabs-menu-container', true)}
                    >
                        {tabs.map((tab, index) => {
                            if (index === 0) return null;

                            const id = tab.Title;

                            return (
                                <TabMenuItem
                                    key={id}
                                    id={id}
                                    onTabClick={onTabChange}
                                    text={tab.Title}
                                    isActive={id === activeTab}
                                    componentClasses={styles.tabItem}
                                />
                            );
                        })}
                    </TabsMenu>
                </div>

                {tabData && (
                    <Flex alignItems="flex-start">
                        <div
                            className={styles.fieldSet}
                            {...createDataTestId('form-single-view-column', false)}
                        >
                            {renderControls(leftData)}
                        </div>

                        {!!rightData && (
                            <div
                                className={styles.fieldSet}
                                {...createDataTestId('form-single-view-column', false)}
                            >
                                {renderControls(rightData)}
                            </div>
                        )}
                    </Flex>
                )}
            </div>
        </div>
    );
});
